/**
 * API client per la gestione delle email.
 */

import axios from 'axios';

export interface EmailItem {
  id: string;
  thread_id: string;
  message_id: string;
  from_email: string;
  to_email: string[] | null;
  subject: string | null;
  summary_ai: string | null;
  category: string | null;
  priority: string | null;
  direction: string | null;
  status: string;
  received_at: string;
  tags: string[];
}

export interface AttachmentItem {
  id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  is_image: boolean;
  is_dicom: boolean;
}

export interface EmailDetail extends EmailItem {
  in_reply_to: string | null;
  direction: string | null;
  body_raw: string;
  body_html: string | null;
  auto_reply_draft: string | null;
  auto_reply_approved: boolean;
  needs_patient_confirmation: boolean;
  attachments: AttachmentItem[];
  case_id: string | null;
  patient_name: string | null;
  owner_name: string | null;
}

export interface EmailRaw {
  id: string;
  message_id: string;
  subject: string | null;
  body_raw: string;
  body_html: string | null;
}

export interface CategoryStats {
  category: string;
  count: number;
}

export interface ReclassifyRequest {
  category?: string;
  priority?: string;
  tags?: string[];
}

export interface EmailListParams {
  status?: string;
  category?: string;
  priority?: string;
  vet_id?: string;
  limit?: number;
  offset?: number;
}

const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

export const emailsApi = {
  list: async (params?: EmailListParams): Promise<EmailItem[]> => {
    const response = await api.get<EmailItem[]>('/emails/', { params });
    return response.data;
  },

  get: async (emailId: string): Promise<EmailDetail> => {
    const response = await api.get<EmailDetail>(`/emails/${emailId}`);
    return response.data;
  },

  getRaw: async (emailId: string): Promise<EmailRaw> => {
    const response = await api.get<EmailRaw>(`/emails/${emailId}/raw`);
    return response.data;
  },

  reclassify: async (emailId: string, data: ReclassifyRequest): Promise<{
    message: string;
    email_id: string;
    category: string | null;
    priority: string | null;
    tags: string[];
  }> => {
    const response = await api.post(`/emails/${emailId}/reclassify`, data);
    return response.data;
  },

  getCategoryStats: async (): Promise<CategoryStats[]> => {
    const response = await api.get<CategoryStats[]>('/emails/stats/categories');
    return response.data;
  },

  getAuditTrail: async (emailId: string): Promise<AuditTrail> => {
    const response = await api.get<AuditTrail>(`/emails/${emailId}/audit`);
    return response.data;
  },
};

// ============== Audit Log Types ==============

export interface AuditLogItem {
  id: string;
  email_id: string | null;
  step_name: string;
  timestamp: string;
  input_data: Record<string, unknown> | null;
  output_data: Record<string, unknown> | null;
  llm_prompt: Record<string, unknown> | null;
  llm_response: Record<string, unknown> | null;
  decision: string | null;
  extra_data: Record<string, unknown> | null;
}

export interface AuditTrail {
  email_id: string;
  email_subject: string | null;
  email_status: string;
  email_category: string | null;
  logs: AuditLogItem[];
}
